// @ts-nocheck

import { useState, useEffect, useRef } from "react";
import { supabaseClient } from "@/lib/supabase/client"
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@/lib/utils/cn";
import { createBrowserSupabaseClient } from "@/lib/supabase/client";
import { Avatar } from "@/components/ui/Avatar";
import { VerifiedBadge } from "@/components/ui/VerifiedBadge";
import { IconSearch, IconSend, IconMessage, IconPlus, IconImage, IconSmile, IconX } from "@/components/ui/Icons";
import { formatDistanceToNow } from "date-fns";

interface User { id: string; username: string; display_name: string; avatar_url: string | null; is_verified?: boolean; }
interface Message { id: string; sender_id: string; content: string; created_at: string; read: boolean; }
interface Conversation { id: string; participant: User; last_message?: string; last_at?: string; unread?: number; }

export function MessagesClient({ currentUser }: { currentUser: User }) {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selected, setSelected] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMsg, setNewMsg] = useState("");
  const [sending, setSending] = useState(false);
  const [search, setSearch] = useState("");
  const bottomRef = useRef<HTMLDivElement>(null);
  const supabase = supabaseClient;

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const sendMessage = async () => {
    if (!newMsg.trim() || !selected || sending) return;
    setSending(true);
    const optimistic: Message = {
      id: Date.now().toString(), sender_id: currentUser.id,
      content: newMsg.trim(), created_at: new Date().toISOString(), read: false,
    };
    setMessages(prev => [...prev, optimistic]);
    setNewMsg("");
    try {
      // @ts-ignore
      await supabase.from("messages").insert({
        sender_id: currentUser.id,
        receiver_id: selected.participant.id,
        content: optimistic.content,
      } as any);
    } catch { /* silently fail for demo */ }
    finally { setSending(false); }
  };

  return (
    <div className="flex h-[calc(100dvh-3.5rem)] overflow-hidden">
      {/* Sidebar */}
      <div className={cn("flex flex-col border-r border-border bg-surface w-full max-w-sm", selected && "hidden sm:flex")}>
        {/* Header */}
        <div className="p-4 border-b border-border">
          <div className="flex items-center justify-between mb-3">
            <h1 className="text-lg font-bold text-foreground">Messages</h1>
            <button className="btn-ghost btn-sm w-9 px-0 rounded-xl"><IconPlus className="w-4 h-4" /></button>
          </div>
          <div className="relative">
            <IconSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
            <input value={search} onChange={e => setSearch(e.target.value)}
              className="input pl-9 text-sm h-9" placeholder="Search messages…" />
          </div>
        </div>

        {/* Conversations list */}
        <div className="flex-1 overflow-y-auto">
          {conversations.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-48 gap-3 text-center px-6">
              <div className="w-12 h-12 rounded-2xl bg-muted flex items-center justify-center">
                <IconMessage className="w-6 h-6 text-text-muted" />
              </div>
              <div>
                <p className="text-sm font-medium text-foreground">No messages yet</p>
                <p className="text-xs text-text-muted mt-1">Start a conversation with someone</p>
              </div>
            </div>
          ) : (
            conversations.map(conv => (
              <button key={conv.id} onClick={() => setSelected(conv)}
                className={cn("w-full flex items-center gap-3 p-4 hover:bg-muted/50 transition-colors text-left border-b border-border-subtle last:border-0",
                  selected?.id === conv.id && "bg-brand-muted")}>
                <Avatar src={conv.participant.avatar_url} alt={conv.participant.display_name} size="md" fallback={conv.participant.display_name[0]} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-1 min-w-0">
                      <p className="text-sm font-semibold text-foreground truncate">{conv.participant.display_name}</p>
                      {conv.participant.is_verified && <VerifiedBadge size="xs" />}
                    </div>
                    {conv.last_at && <p className="text-xs text-text-subtle flex-shrink-0">{formatDistanceToNow(new Date(conv.last_at), { addSuffix: false })}</p>}
                  </div>
                  <p className="text-xs text-text-muted truncate">{conv.last_message || "Start a conversation"}</p>
                </div>
                {conv.unread ? <span className="w-5 h-5 rounded-full bg-brand text-white text-xs flex items-center justify-center flex-shrink-0">{conv.unread}</span> : null}
              </button>
            ))
          )}
        </div>
      </div>

      {/* Chat area */}
      <div className={cn("flex-1 flex flex-col", !selected && "hidden sm:flex")}>
        {selected ? (
          <>
            {/* Chat header */}
            <div className="flex items-center gap-3 p-4 border-b border-border bg-surface-elevated">
              <button className="sm:hidden btn-ghost btn-sm w-9 px-0 rounded-xl" onClick={() => setSelected(null)}>
                <IconX className="w-4 h-4" />
              </button>
              <Avatar src={selected.participant.avatar_url} alt={selected.participant.display_name} size="sm" fallback={selected.participant.display_name[0]} />
              <div>
                <div className="flex items-center gap-1.5">
                  <p className="text-sm font-semibold text-foreground">{selected.participant.display_name}</p>
                  {selected.participant.is_verified && <VerifiedBadge size="xs" />}
                </div>
                <p className="text-xs text-text-muted">@{selected.participant.username}</p>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {messages.length === 0 && (
                <div className="flex flex-col items-center justify-center h-full gap-2 text-center">
                  <Avatar src={selected.participant.avatar_url} alt={selected.participant.display_name} size="xl" fallback={selected.participant.display_name[0]} />
                  <p className="text-sm font-medium text-foreground mt-2">Start a conversation with {selected.participant.display_name}</p>
                  <p className="text-xs text-text-muted">Your messages are private.</p>
                </div>
              )}
              {messages.map(msg => {
                const isOwn = msg.sender_id === currentUser.id;
                return (
                  <motion.div key={msg.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                    className={cn("flex", isOwn ? "justify-end" : "justify-start")}>
                    <div className={cn("max-w-[75%] px-4 py-2.5 rounded-2xl text-sm",
                      isOwn ? "bg-brand text-white rounded-br-sm" : "bg-surface-elevated text-foreground rounded-bl-sm border border-border-subtle")}>
                      <p>{msg.content}</p>
                      <p className={cn("text-xs mt-1", isOwn ? "text-white/60" : "text-text-subtle")}>
                        {formatDistanceToNow(new Date(msg.created_at), { addSuffix: true })}
                      </p>
                    </div>
                  </motion.div>
                );
              })}
              <div ref={bottomRef} />
            </div>

            {/* Input */}
            <div className="p-4 border-t border-border bg-surface-elevated">
              <div className="flex items-end gap-2">
                <div className="flex-1 relative">
                  <textarea
                    value={newMsg}
                    onChange={e => setNewMsg(e.target.value)}
                    onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
                    placeholder={`Message ${selected.participant.display_name}…`}
                    rows={1}
                    className="input resize-none py-2.5 pr-20 max-h-32"
                    style={{ overflowY: newMsg.split("\n").length > 3 ? "auto" : "hidden" }}
                  />
                  <div className="absolute right-2 bottom-2 flex items-center gap-1">
                    <button className="p-1.5 text-text-muted hover:text-foreground transition-colors rounded-lg"><IconImage className="w-4 h-4" /></button>
                    <button className="p-1.5 text-text-muted hover:text-foreground transition-colors rounded-lg"><IconSmile className="w-4 h-4" /></button>
                  </div>
                </div>
                <button onClick={sendMessage} disabled={!newMsg.trim() || sending}
                  className="btn-primary btn-md w-10 px-0 rounded-xl flex-shrink-0 disabled:opacity-50">
                  <IconSend className="w-4 h-4" />
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center gap-4 text-center px-6">
            <div className="w-16 h-16 rounded-2xl bg-brand-muted flex items-center justify-center">
              <IconMessage className="w-8 h-8 text-brand" />
            </div>
            <div>
              <p className="text-base font-semibold text-foreground">Your messages</p>
              <p className="text-sm text-text-muted mt-1">Select a conversation or start a new one</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
